/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import CollectionBanner from 'bundles/collections/components/CollectionBanner';
import CollectionItemsSection from 'bundles/collections/components/CollectionItemsSection';
import RegistrationPromptBanner from 'bundles/collections/components/RegistrationPromptBanner';
import type {
  CollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements as CatalogCollectionPage,
  CollectionPageQuery,
  CollectionPageQueryVariables,
} from 'bundles/collections/components/__generated__/CollectionPageQuery';
import { stripAllTagsFromCMLString } from 'bundles/enterprise-legacy-learner-home/utils/programCommonUtils';
import { CourseraMetatags } from 'bundles/seo';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

const COLLECTION_PAGE_QUERY = gql`
  query CollectionPageQuery($slug: String!) {
    CatalogCollectionPagesV1Resource {
      bySlug(slug: $slug) {
        elements {
          title
          primaryLanguages
          description {
            ... on CatalogCollectionPagesV1_cmlMember {
              cml {
                value
                dtdId
              }
            }
          }
          ...CollectionBannerFragment
          ...CollectionItemsFragment
        }
      }
    }
  }
  ${CollectionBanner.fragments.collectionBannerFragment}
  ${CollectionItemsSection.fragments.collectionItemsFragment}
`;
const styles = {
  collectionsPage: css`
    overflow-x: hidden;
  `,
};

type PropsFromRouter = {
  slug: string;
  isInternationalSubdomain: boolean;
};

type PropsFromCollectionPageQuery = {
  catalogCollectionPage?: CatalogCollectionPage | null;
};

type Props = PropsFromRouter & PropsFromCollectionPageQuery;

type Context = {
  router: InjectedRouter;
};

class CollectionPage extends React.Component<Props> {
  constructor(props: Props, context: Context) {
    super(props, context);
    const { catalogCollectionPage } = props;
    if (inServerContext && !catalogCollectionPage) {
      // if the slug is invalid, we're throwing an error and node render will propagate the error to edge
      // to render a static 404 page
      throw new NotFoundError();
    }
  }

  render() {
    const { catalogCollectionPage, isInternationalSubdomain } = this.props;

    if (!catalogCollectionPage) {
      return null;
    }

    const descriptionText =
      catalogCollectionPage &&
      catalogCollectionPage.description &&
      stripAllTagsFromCMLString(catalogCollectionPage.description.cml.value);

    const productPrimaryLanguage = catalogCollectionPage.primaryLanguages
      ? catalogCollectionPage.primaryLanguages[0]
      : 'en';

    return (
      <div css={styles.collectionsPage}>
        <CollectionBanner catalogCollectionPage={catalogCollectionPage} />
        <CollectionItemsSection
          catalogCollectionPage={catalogCollectionPage}
          productPrimaryLanguage={productPrimaryLanguage}
        />
        <RegistrationPromptBanner />
        <CourseraMetatags
          metaNameAndProperties={{
            description: descriptionText ?? undefined,
            productPrimaryLanguage,
            title: catalogCollectionPage.title,
            ogAndTwitterTitle: catalogCollectionPage.title,
            disableCourseraDescriptionSuffix: true,
            disableDescLimit: true,
            disableCrawlerIndexing: isInternationalSubdomain,
          }}
          hrefLangOverrides={() => []}
        />
      </div>
    );
  }
}

export default compose<Props, {}>(
  connectToRouter((router) => {
    return {
      slug: router.params.slug,
      isInternationalSubdomain: getSubdomainDetails(router.location)?.isInternationalSubdomain,
    };
  }),
  waitForGraphQL<PropsFromRouter, CollectionPageQuery, CollectionPageQueryVariables, PropsFromCollectionPageQuery>(
    COLLECTION_PAGE_QUERY,
    {
      options: ({ slug }) => ({
        variables: {
          slug,
        },
      }),
      props: ({ data }) => {
        const catalogCollectionPage = data?.CatalogCollectionPagesV1Resource?.bySlug?.elements?.[0];

        return {
          catalogCollectionPage,
        };
      },
    }
  )
)(CollectionPage);
